import { useEffect } from 'react';

/**
 * Critical Resource Hints Component
 * Implements aggressive resource preloading and prefetching for maximum performance
 */
export function CriticalResourceHints() {
  useEffect(() => {
    // DNS prefetch for external domains
    const dnsPrefetchDomains = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://www.google-analytics.com',
      'https://www.googletagmanager.com',
      'https://images.unsplash.com',
      'https://api.netlify.com'
    ];

    dnsPrefetchDomains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = domain;
      document.head.appendChild(link);
    });

    // Preconnect to critical domains
    const preconnectDomains = [
      { href: 'https://fonts.googleapis.com', crossorigin: true },
      { href: 'https://fonts.gstatic.com', crossorigin: true },
      { href: 'https://images.unsplash.com', crossorigin: false }
    ];

    preconnectDomains.forEach(({ href, crossorigin }) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      if (crossorigin) {
        link.crossOrigin = 'anonymous';
      }
      document.head.appendChild(link);
    });

    // Preload critical resources
    const criticalResources = [
      { href: '/logo.png', as: 'image', type: 'image/png', priority: 'high' },
      { href: '/rs-logo.svg', as: 'image', type: 'image/svg+xml', priority: 'high' },
      { href: '/panama-city-night.jpg', as: 'image', type: 'image/jpeg', priority: 'medium' }
    ];

    criticalResources.forEach(({ href, as, type, priority }) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = href;
      link.as = as;
      if (type) link.type = type;
      if (priority) link.setAttribute('fetchpriority', priority);
      document.head.appendChild(link);
    });

    // Prefetch likely navigation targets
    const prefetchRoutes = [
      '/blog',
      '/podcast',
      '/content/Guida Residenza a Panama per Italiani.pdf'
    ];

    // Delay prefetch to avoid competing with critical resources
    setTimeout(() => {
      prefetchRoutes.forEach(route => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = route;
        document.head.appendChild(link);
      });
    }, 2000);

    // Preload critical CSS
    const criticalCSS = `
      /* Critical above-the-fold styles */
      body {
        margin: 0;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        line-height: 1.6;
        color: #1f2937;
        background-color: #ffffff;
      }
      
      .hero-section {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      }
      
      .nav-header {
        position: fixed;
        top: 0;
        width: 100%;
        z-index: 1000;
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(10px);
      }
      
      /* Prevent layout shift */
      img {
        max-width: 100%;
        height: auto;
      }
      
      /* Loading states */
      .loading-skeleton {
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: loading 1.5s infinite;
      }
      
      @keyframes loading {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `;

    const style = document.createElement('style');
    style.textContent = criticalCSS;
    style.setAttribute('data-critical', 'true');
    document.head.appendChild(style);

    // Resource hints for dynamic imports
    const modulePreloads = [
      '/src/pages/BlogPage.tsx',
      '/src/pages/PodcastPage.tsx',
      '/src/components/ContactForm.tsx'
    ];

    modulePreloads.forEach(module => {
      const link = document.createElement('link');
      link.rel = 'modulepreload';
      link.href = module;
      document.head.appendChild(link);
    });

    // Cleanup function
    return () => {
      // Remove resource hints if component unmounts
      const resourceHints = document.querySelectorAll('link[rel="dns-prefetch"], link[rel="preconnect"], link[rel="prefetch"]');
      resourceHints.forEach(link => link.remove());
      
      const criticalStyles = document.querySelector('style[data-critical="true"]');
      if (criticalStyles) {
        criticalStyles.remove();
      }
    };
  }, []);

  return null;
}

export default CriticalResourceHints;